import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import Waypoint from 'react-waypoint';
import { useAdminUserSession } from '../../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { invalidateGetPhoneNumbers, useGetPhoneNumbers } from '../../../../../queries';
import { useReconcileTextingContacts } from '../../../../../queries/AdminTools';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../../web/components/Modal';
import { ChevronDown } from '../../../../../web/components/svgs/icons/ChevronDown';
import { ChevronRight } from '../../../../../web/components/svgs/icons/ChevronRight';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ManageUsersModal, TextingRow } from './presentation';
import { styleSheet } from './styles';

const BasePhoneNumbersFilter: Api.TelephonyConfigurationFilter = {
	criteria: [
		{
			property: Api.TelephonyConfigurationFilterProperty.Capability,
			value: 'Text',
		},
		{
			property: Api.TelephonyConfigurationFilterProperty.ConnectionState,
			value: Api.ConnectionState.Connected,
		},
	],
	op: Api.FilterOperator.And,
};

export const TextingManagement = ({ account }: { account: Api.IAccount }) => {
	const impersonationContext = React.useMemo(() => ({ account: { id: account.id } }), [account.id]);
	const [numberToManage, setNumberToManage] = React.useState<Api.ITelephonyConfiguration>(null);
	const [showReconcileModal, setShowReconcileModal] = React.useState(false);
	const [isSupportToolsExpanded, setIsSupportToolsExpanded] = React.useState(false);
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging('TextingManagement');

	const phoneNumbersQuery = useGetPhoneNumbers({
		filterRequest: BasePhoneNumbersFilter,
		impersonationContext,
		pageSize: 100,
		sortBy: 'PhoneNumberScope',
		sort: 'asc',
	});

	const canReconcile = React.useMemo(
		() =>
			userSession.hasPermission(AdminPermission.SupportLevel3) ||
			userSession.hasPermission(AdminPermission.OnlyForDevelopers),
		[userSession]
	);

	const closeReconcileModal = React.useCallback(() => {
		setShowReconcileModal(false);
	}, []);

	const reconcileMutation = useReconcileTextingContacts({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('ReconcileTextingContacts-Error', error);
			closeReconcileModal();
		},
		onSuccess: () => {
			toast.success('Texting contacts reconciliation started successfully');
			logEvent('ReconcileTextingContacts-Success', { accountId: account.id });
			closeReconcileModal();
		},
		userSession,
	});

	const handleReconcileClick = React.useCallback(() => {
		setShowReconcileModal(true);
	}, []);

	const handleConfirmReconcile = React.useCallback(() => {
		logEvent('ReconcileTextingContacts-Start', { accountId: account.id });
		reconcileMutation.mutate({ accountId: account.id });
	}, [account.id, logEvent, reconcileMutation]);

	const toggleSupportTools = React.useCallback(() => {
		setIsSupportToolsExpanded(prev => !prev);
	}, []);

	if (!phoneNumbersQuery.data) {
		return null;
	}

	const onModalSave = () => {
		setNumberToManage(null);
		invalidateGetPhoneNumbers(impersonationContext, BasePhoneNumbersFilter);
	};

	const onModalCancel = () => {
		setNumberToManage(null);
	};

	return (
		<section className={css(styleSheet.container)}>
			{canReconcile ? (
				<div className={css(styleSheet.supportToolsSection)}>
					<button className={css(styleSheet.supportToolsHeader)} onClick={toggleSupportTools}>
						{isSupportToolsExpanded ? (
							<ChevronDown className={css(styleSheet.chevron)} width={16} height={16} />
						) : (
							<ChevronRight className={css(styleSheet.chevron)} width={16} height={16} />
						)}
						<span className={css(styleSheet.supportToolsTitle)}>Support Tools</span>
					</button>
					{isSupportToolsExpanded ? (
						<div className={css(styleSheet.supportToolsContent)}>
							<div className={css(styleSheet.toolItem)}>
								<button className={css(baseStyleSheet.ctaButtonSmall)} onClick={handleReconcileClick}>
									Reconcile Texting Contacts
								</button>
								<span className={css(styleSheet.toolDescription)}>
									Updates conversation names when contact information has changed
								</span>
							</div>
						</div>
					) : null}
				</div>
			) : null}
			{showReconcileModal ? (
				<Modal isOpen={true} onRequestClose={closeReconcileModal} useDefaultHeader={true}>
					<div className={css(styleSheet.modalContainer)}>
						<h2 className={css(styleSheet.modalHeader)}>Confirm Reconciliation</h2>
						<p className={css(styleSheet.modalDescription)}>
							Are you sure you want to reconcile texting contacts for <strong>{account.companyName}</strong>?
						</p>
						<p className={css(styleSheet.modalWarning)}>
							This will update all conversation names to match the current contact information. This action may take a
							few moments to complete.
						</p>
						<div className={css(styleSheet.modalActions)}>
							<button
								className={css(baseStyleSheet.ctaButton)}
								onClick={handleConfirmReconcile}
								disabled={reconcileMutation.isPending}
							>
								{reconcileMutation.isPending ? (
									<>
										<LoadingSpinner type='tiny' className={css(styleSheet.buttonSpinner)} />
										Reconciling...
									</>
								) : (
									'Confirm'
								)}
							</button>
							<button
								className={css(baseStyleSheet.ctaButtonReverse)}
								onClick={closeReconcileModal}
								disabled={reconcileMutation.isPending}
							>
								Cancel
							</button>
						</div>
					</div>
				</Modal>
			) : null}
			<table className={css(styleSheet.table)}>
				<thead className={css(styleSheet.tableHead)}>
					<tr className={css(styleSheet.tableRow)}>
						<th className={css(styleSheet.headerCell)} />
						<th className={css(styleSheet.headerCell, styleSheet.phoneNumberHeaderCell)}>Phone Number</th>
						<th className={css(styleSheet.headerCell)}>Connected User(s)</th>
						<th className={css(styleSheet.headerCell)}>Provider</th>
						<th className={css(styleSheet.headerCell)}>Campaign Id</th>
					</tr>
				</thead>
				<tbody>
					{phoneNumbersQuery.data?.pages.map(page =>
						page.values.map(phoneNumber => {
							return (
								<TextingRow
									key={phoneNumber.id}
									phoneNumber={phoneNumber}
									impersonationContext={impersonationContext}
									setNumberToManage={setNumberToManage}
								/>
							);
						})
					)}
					{!phoneNumbersQuery.isPending && <tr className={css(styleSheet.expander)} />}
				</tbody>
			</table>

			{phoneNumbersQuery.hasNextPage && !phoneNumbersQuery.isFetchingNextPage ? (
				<Waypoint bottomOffset='-200px' onEnter={() => phoneNumbersQuery.fetchNextPage()} />
			) : null}

			{numberToManage ? (
				<ManageUsersModal
					phoneNumber={numberToManage}
					allPhoneNumbers={phoneNumbersQuery.data}
					onSave={onModalSave}
					onCancel={onModalCancel}
					impersonationContext={impersonationContext}
				/>
			) : null}
		</section>
	);
};
